import { useState } from "react";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";

import MDInput from "components/MDInput";
import ActionDialog from "components/admin/ActionDialog";
import { useRunner } from "components/admin/hooks";
import api from "api/client";

function SummonBossDialog({ boss, maps: allMaps, onClose, onDone }) {
  const [run, busy] = useRunner();
  const maps = allMaps.filter((m) => m.hasMobs && m.normalZones?.length > 0);
  const [map, setMap] = useState(() => maps.find((m) => boss.maps?.[0]?.mapID === m.mapId) || null);
  const [zoneId, setZoneId] = useState(map?.normalZones?.[0] ?? "");
  const [minutes, setMinutes] = useState(15);
  const [announce, setAnnounce] = useState(true);

  const submit = async () => {
    const { ok } = await run(
      () =>
        api.post("/api/admin/boss/summon", {
          bossId: boss.bossId,
          mapId: map.mapId,
          zoneId: Number(zoneId),
          minutes: Number(minutes),
          announce,
        }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={`Gọi ${boss.name} ra map`}
      color="error"
      submitText={announce ? "Gọi ra & báo toàn server" : "Gọi ra"}
      busy={busy}
      disabled={!map || zoneId === ""}
      onClose={onClose}
      onSubmit={submit}
    >
      <Autocomplete
        options={maps}
        value={map}
        onChange={(e, value) => {
          setMap(value);
          setZoneId(value?.normalZones?.[0] ?? "");
        }}
        getOptionLabel={(m) => m.name}
        isOptionEqualToValue={(a, b) => a.mapId === b.mapId}
        renderInput={(params) => <MDInput {...params} label="Map (gõ tên)" />}
      />
      <MDInput
        select
        label="Khu"
        value={zoneId}
        disabled={!map}
        onChange={(e) => setZoneId(e.target.value)}
        SelectProps={{ sx: { height: 44 } }}
      >
        {(map?.normalZones || []).map((z) => (
          <MenuItem key={z} value={z}>
            Khu {z}
          </MenuItem>
        ))}
      </MDInput>
      <MDInput
        type="number"
        label="Boss ở lại bao lâu (phút, 1 - 1440)"
        value={minutes}
        inputProps={{ min: 1, max: 1440 }}
        onChange={(e) => setMinutes(e.target.value)}
      />
      <MDBox display="flex" gap={1} flexWrap="wrap" mt={-1}>
        {[5, 15, 30, 60, 120].map((m) => (
          <Chip
            key={m}
            size="small"
            variant={Number(minutes) === m ? "filled" : "outlined"}
            color={Number(minutes) === m ? "info" : "default"}
            label={m < 60 ? `${m} phút` : `${m / 60} giờ`}
            onClick={() => setMinutes(m)}
          />
        ))}
      </MDBox>
      <FormControlLabel
        control={<Switch checked={announce} onChange={(e) => setAnnounce(e.target.checked)} />}
        label="Hiện banner báo cho mọi người chơi đang online"
      />
      {map && zoneId !== "" && (
        <Alert severity="warning">
          {boss.name} sẽ xuất hiện ở{" "}
          <b>
            {map.name} khu {zoneId}
          </b>{" "}
          trong {minutes} phút
          {announce ? ", có banner báo toàn máy chủ." : ", không báo ai."}
        </Alert>
      )}
      <Alert severity="info">
        Boss đứng vào chỗ quái trống (hoặc thay một con quái không trong trận). Không ảnh hưởng bộ
        đếm boss của khu; hết giờ chưa bị hạ thì boss bỏ đi và chia quà như bình thường.
      </Alert>
    </ActionDialog>
  );
}

SummonBossDialog.propTypes = {
  boss: PropTypes.shape({
    bossId: PropTypes.number.isRequired,
    name: PropTypes.string,
    maps: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  maps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default SummonBossDialog;
